import type { ReactNode } from 'react'
import type { SupportTicketDetail } from '@/api/supportApi'
import { TYPE_LABEL, dueIn, progressSteps, type StepState } from '@/lib/support/supportView'
import { cn } from '@/lib/utils'

const DOT: Record<StepState, string> = {
  done: 'bg-emerald-500',
  current: 'bg-amber-500 ring-4 ring-amber-100',
  todo: 'bg-muted-foreground/30',
}

/** Right pane: progress tracker and the ticket's facts. */
export function SupportFacts({ ticket, now }: { ticket: SupportTicketDetail; now: number }) {
  const due = ticket.sla?.dueAt
  return (
    <aside className='min-h-0 overflow-y-auto bg-muted/30 px-4 py-4 text-sm'>
      <Heading first>Progress</Heading>
      <ol className='flex flex-col gap-2'>
        {progressSteps(ticket).map((s) => (
          <li key={s.label} className={cn('flex items-center gap-2', s.state === 'todo' ? 'text-muted-foreground' : 'text-foreground', s.state === 'current' && 'font-semibold text-amber-700')}>
            <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', DOT[s.state])} aria-hidden />
            {s.label}
          </li>
        ))}
      </ol>

      <Heading>Details</Heading>
      <Fact label='Type'>{TYPE_LABEL[ticket.type] ?? ticket.type}</Fact>
      <Fact label='Priority'>{ticket.priority}</Fact>
      <Fact label='Resolution due'>
        {ticket.status === 'resolved' || ticket.status === 'closed' ? '—' : due ? (ticket.sla?.clockRunning ? dueIn(due, now) : 'Paused') : '—'}
      </Fact>
      <Fact label='Handled by'>{ticket.assignee?.name ?? 'Title21 Support'}</Fact>
      <Fact label='Tenant'>{ticket.tenant?.name ?? '—'}</Fact>
      {ticket.slaPolicy ? <Fact label='Service level'>{ticket.slaPolicy.name}</Fact> : null}
    </aside>
  )
}

function Heading({ children, first }: { children: ReactNode; first?: boolean }) {
  return <h3 className={cn('mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground', first ? '' : 'mt-5')}>{children}</h3>
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='flex justify-between gap-3 py-1 text-muted-foreground'>
      <span>{label}</span>
      <span className='text-right font-medium text-foreground'>{children}</span>
    </div>
  )
}
